import {useEffect, useState, useCallback, useMemo, useRef, type CSSProperties, type DragEvent} from 'react'
import {Box, Typography, useMediaQuery} from '@mui/material'
import {useTranslation} from 'react-i18next'
import ViewportV2 from '../../common/components/ViewportV2'
import EditorErrorBoundary from './errorBoundary/EditorErrorBoundary'
import {useToast} from '../../common/components/toast/toastContext'
import RightSidebarMenu from './rightSidebar/RightSidebarMenu'
import LeftSidebarMenu from './leftSidebar/LeftSidebarMenu'
import ExportFrameOverlay from './rightSidebar/ExportFrameOverlay'
import TextOverlay from '../../common/components/TextOverlay'
import UndoRedoToolbar from './topMenuBar/UndoRedoToolbar'
import SceneToolbar from './topMenuBar/SceneToolbar'
import EditorTopBar from './topMenuBar/EditorTopBar'
import CameraPresetBar from './topMenuBar/CameraPresetBar'
import useUndoRedoShortcuts from './topMenuBar/useUndoRedoShortcuts'
import MobileEditorDrawer from './mobile/MobileEditorDrawer'
import {MOBILE_BREAKPOINT} from './constants/responsive'
import {MODEL_LOADING_TIMEOUT_MS} from './constants/editorLoading'
import {useAppDispatch} from '../../common/store'
import {setScreenImageUrl} from '../../common/components/store/scenesSlice'
import {useAppTokens} from '../theme'
import type {ThemeTokens} from '../theme'
import type {RenderExportFn} from '../../common/components/ExportHandler'
import {reportError} from '../../common/errors/reportError'
import LoadingLogo from './LoadingLogo'

// ── Helpers ───────────────────────────────────────────────────────

const loadImageFile = (file: File): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
    }
    reader.readAsDataURL(file)
  })

// ── EditorPage — main layout: left sidebar + 3D viewport + right sidebar ──

const EditorPage = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()
  const toast = useToast()
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT)
  useUndoRedoShortcuts()
  const [loading, setLoading] = useState(true)
  const [workerBusy, setWorkerBusy] = useState(true)
  const [exportRendering, setExportRendering] = useState(false)

  useEffect(() => {
    const handleLost = () => {
      reportError(new Error('WebGL context lost'), 'webgl-lost')
      toast.error(t('errors.webglLost'))
    }
    window.addEventListener('app:webgl-lost', handleLost)
    return () => window.removeEventListener('app:webgl-lost', handleLost)
  }, [t, toast])

  const renderExportRef = useRef<RenderExportFn | null>(null)

  const handleLoadingChange = useCallback((isLoading: boolean) => {
    setLoading(isLoading)
  }, [])

  const handleWorkerBusyChange = useCallback((busy: boolean) => {
    setWorkerBusy(busy)
  }, [])

  // Clear the loading overlay when the viewport subtree crashes — otherwise the
  // blur (rendered outside the error boundary) covers the error card forever.
  const handleViewportError = useCallback(() => {
    setLoading(false)
    setWorkerBusy(false)
  }, [])

  // Safety net: if loading never resolves (hung worker, missing callback),
  // force the overlay down after a timeout and surface an error.
  useEffect(() => {
    if (!loading) return
    const timer = window.setTimeout(() => {
      setLoading(false)
      setWorkerBusy(false)
      toast.error(t('errors.modelLoadTimeout'))
    }, MODEL_LOADING_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [loading, t, toast])

  const handleExportReady = useCallback((renderExport: RenderExportFn) => {
    renderExportRef.current = renderExport
  }, [])

  // ── Viewport drag-and-drop for screen image ─────────────────────

  const handleViewportDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
  }, [])

  const handleViewportDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer.files[0]
      if (!file || !file.type.startsWith('image/')) return
      const dataUrl = await loadImageFile(file)
      dispatch(setScreenImageUrl(dataUrl))
    },
    [dispatch],
  )

  const layoutSx = useMemo(() => buildLayoutStyles(tokens), [tokens])

  return (
    <div style={layoutSx.root}>
      <EditorTopBar />
      <div style={layoutSx.body}>
        {!isMobile && <LeftSidebarMenu />}
        <div style={layoutSx.centerColumn}>
          {!isMobile && <SceneToolbar />}
          <div style={layoutSx.viewport} onDragOver={handleViewportDragOver} onDrop={handleViewportDrop}>
            <EditorErrorBoundary onError={handleViewportError}>
              <ViewportV2
                loading={loading}
                onLoadingChange={handleLoadingChange}
                onWorkerBusyChange={handleWorkerBusyChange}
                onExportReady={handleExportReady}
              />
            </EditorErrorBoundary>
            <UndoRedoToolbar />
            {!isMobile && <CameraPresetBar />}
            <TextOverlay />
            <ExportFrameOverlay />
            {/* Opaque overlay to hide scene switching during multi-scene export */}
            {exportRendering && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: tokens.surface.background,
                  zIndex: 8,
                }}
              />
            )}
            {loading && (
              <Box sx={overlayStyles.overlay}>
                <LoadingLogo size={48} />
                <Typography sx={overlayStyles.label}>{t('loading.generatingModel')}</Typography>
              </Box>
            )}
          </div>
        </div>
        {!isMobile && (
          <RightSidebarMenu
            renderExportRef={renderExportRef}
            onExportRendering={setExportRendering}
            workerBusy={workerBusy}
          />
        )}
      </div>
      {isMobile && (
        <MobileEditorDrawer
          renderExportRef={renderExportRef}
          onExportRendering={setExportRendering}
          workerBusy={workerBusy}
        />
      )}
    </div>
  )
}

const buildLayoutStyles = (tokens: ThemeTokens): Record<string, CSSProperties> => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100vw',
    height: '100dvh',
    background: tokens.surface.background,
  },
  body: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },
  centerColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  viewport: {
    flex: 1,
    position: 'relative',
  },
})

const overlayStyles = {
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.5,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: 10,
    pointerEvents: 'none',
  },
  label: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: '0.02em',
  },
} as const

export default EditorPage
